import ComponentManager from '@/Management/ComponentManager';
import ToolBarLinkViewModel from '@/Components/Links/ToolBarLinkViewModel';
import GlobalConfiguration from '@/Management/GlobalConfiguration';
import SearchListViewViewModelType from '@/ViewModels/Views/SearchListViewViewModel';
import * as CoreFrameDTOs from "coreframetypings/dto";
import Utilities from '@/Utility/Utilities';
import RADEnvironmentProxy from '@/Communication/RADEnvironmentProxy';
import Loader from '@/Utility/Loader';
import NavigationService from '@/Management/NavigationService';
import { IRefreshable } from '#/DefaultViewExtensionInterfaces';
import SearchListViewViewFactory from '@/Factories/ViewFactories/SearchListViewViewFactory';
import AssociationHelper from '@/Utility/Association/AssociationHelper';

export default class FindAndDisconnectLinkViewModel extends ToolBarLinkViewModel {
    
    protected dataSource: IRefreshable;
    protected listView: KnockoutObservable<CoreFrameDTOs.IListViewDTO>;
    protected contextPdoId: KnockoutObservable<any>;
    protected contextTypeName: KnockoutObservable<string>;
    protected contextRelationName: KnockoutObservable<string>;
    protected typeName: KnockoutObservable<string>;
    protected detailViewItem: CoreFrameDTOs.IDVGItemDTO;
    protected searchViewFilter: string;
    private viewFactorySearchListView: SearchListViewViewFactory;

    public constructor(protected params: any) {
        super($.extend(params,
            {
                ergName: 'DISCONNECT',
                tooltipErgName: 'DISCONNECT_TOOLTIP',
                imageSource: GlobalConfiguration.inlineImages.TOOLBAR_DISCONNECT,
                locator: 'disconnect'
            }));

        this.contextPdoId = this.observableOrDefault(params.contextPdoId, this.context.genericObject.pdoId);
        this.contextTypeName = this.observableOrDefault(params.contextTypeName, this.context.genericObject.pdTypeName);

        this.setRequiredObservables();
        this.typeName = this.defineComputed(() => {
            const paramTypeName = ko.unwrap(params.pdTypeName);

            if (!Utilities.isNullOrUndefined(paramTypeName))
                return paramTypeName;

            return this.listView().typeName;
        });

        if (params.contextRelationName)
            this.contextRelationName = this.observableOrDefault(params.contextRelationName);
        else
            this.contextRelationName = this.defineComputed(() => this.listView().relationName);

        this.dataSource = this.paramOrDefault(params.dataSource, null);
    }

    /**
     * @override
     */
    protected createIsVisibleObservable(): KnockoutObservable<boolean> {
        if (!Utilities.isNullOrUndefined(this.params.isVisible)) {
            return this.observableOrDefault(this.params.isVisible);
        }

        this.setRequiredObservables();

        return this.createWritableComputed(() => !this.params.context?.isSummaryViewMode() && this.listView().showButtonDisconnect);
    }

    /**
     * @override
     */
    public onClick(openInNewWindow?: boolean, event?: JQuery.Event): void {
        const listView = this.listView();
        
        // Diese Expression wird für das Verbinden von neuen Objekten verwendet 
        // und nicht für die Suche nach bereits verbundenen Objekten.
        listView.staticFilterExpression = '';
        
        this.openSearchListView(listView);
    }

    /**
     * Öffnet die Suchliste mit den bereits verbundenen Objekten
     * @param listView
     */
    protected openSearchListView(listView: CoreFrameDTOs.IListViewDTO): void {
        const SearchListViewViewModel = ComponentManager.getViewModel<typeof SearchListViewViewModelType>('SearchListViewViewModel');
        if (this.viewFactorySearchListView == null) {
            this.viewFactorySearchListView = NavigationService.getInstance().loadViewFactoryIsolated('SearchListView', {
                viewModel: new SearchListViewViewModel({
                    parentLocator: this.toolBar?.parentLocator(),
                    locator: 'search-view',
                    contextPdoId: this.contextPdoId(),
                    contextTypeName: this.contextTypeName(),
                    showConnectedObjects: true,
                    listView: listView,
                    allowMultiSelect: true,
                    serializedFilter: this.searchViewFilter,
                    callback: (toPdoIds: Array<string>) => {
                        const pdoId = this.contextPdoId();
                        const associationName = this.contextRelationName();
                        const pdTypeName = this.typeName();
                        const nestedContextName = this.context.getNestedContextPath();
                        this.disconnect(pdoId, associationName, pdTypeName, toPdoIds, nestedContextName);
                    },
                    onClose: () => {
                        this.viewFactorySearchListView.viewModel.dataGrid.filter.forceFilterExpressionObjectCalculation();
                        this.searchViewFilter = this.viewFactorySearchListView.viewModel.dataGrid.filter.serializedFilter();
                        this.hasFocus(true);
                        NavigationService.getInstance().removeViewFactoryIsolated(this.viewFactorySearchListView);
                        this.viewFactorySearchListView = null;
                    }
                })
            }, true);
        }
    }

    /**
     * Die Ausführung des eigentlichen Trennens
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected disconnect(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): void {
        $.when(this.context.notifyBeforeUpdatedAssociation(associationName, 'disconnect', { toPdoIds: toPdoIds }))
            .then((doUpdateAssociation) => {
                if (doUpdateAssociation == null || doUpdateAssociation === true) {
                    Loader.getInstance().show();
                    this.callDisconnectOperation(pdoId, associationName, topdTypeName, toPdoIds, nestedContextName)
                        .then((changeObject: CoreFrameDTOs.IChangeObjectDTO) => {
                            this.disconnectCallback(changeObject);
                        }).always(() => {
                            Loader.getInstance().hide();
                        });
                }
            });
    }

    /**
     * Ruft die Methode zum Trennen auf
     * @param pdoId
     * @param associationName
     * @param topdTypeName
     * @param toPdoIds
     * @param nestedContextName
     */
    protected callDisconnectOperation(pdoId: string, associationName: string, topdTypeName: string, toPdoIds: Array<string>, nestedContextName: string): JQueryPromise<any> {
        return RADEnvironmentProxy.getInstance().disconnectMany({
            pdoID: pdoId,
            assoziationName: associationName,
            topdTypeName: topdTypeName,
            toPDOIDs: toPdoIds,
            nestedContextName: nestedContextName
        }).promise();
    }

    /**
     * Das Callback, welches nach dem Disconnect ausgeführt wird
     * @param callbackResult
     */
    protected disconnectCallback(callbackResult: any): void {
        // Auch im Fehlerfall kann sich die Assoziation geändert haben, da alle Objekte bis zum Fehler getrennt werden
        // Nachfolgende Elemente (ab Index, wo der Fehler auftritt) werden nicht mehr getrennt
        this.validateStatus(callbackResult as CoreFrameDTOs.IChangeObjectDTO);
        // Alle geänderten Assoziationen werden geladen, auch wenn sie Lazy sind
        AssociationHelper.activateDataLoading(this.context, callbackResult.touchedMembers, this.listView().relationName);
        this.dataSource?.refresh();
        this.context.genericObject.isModified(true);
        this.context.notifyUpdatedAssociation(this.listView().relationName);
        this.context.notifyFactoryRefreshed(this.detailViewItem);
    }

    /**
     * Da bereits im Konstruktor der Oberklassen Methoden aufgerufen werden, die auf Member dieser Klasse zugreifen
     * muss dies in eine Methode ausgelagert werden
     */
    protected setRequiredObservables(): void {
        if (Utilities.isNullOrUndefined(this.listView)) {
            this.listView = this.observableOrDefault(this.params.listView);
        }

        if (Utilities.isNullOrUndefined(this.searchViewFilter)) {
            this.searchViewFilter = '';
        }

        if (Utilities.isNullOrUndefined(this.detailViewItem)) {
            this.detailViewItem = this.paramOrDefault(this.params.detailViewItem, null);
        }
    }
}

ComponentManager.setViewModel('FindAndDisconnectLink', FindAndDisconnectLinkViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');
